import React, { useContext, useEffect, useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Stack,
  TextField,
  Typography,
  Toolbar,
  RadioGroup,
  Radio,
  FormControlLabel,
  InputLabel,
  Box,
} from '@mui/material';

import { DraftManualGIQContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import { deleteQuestion, updateQuestion } from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/manageGIQ';
import useGIQ from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/useGIQ';
import { type ServerErrorResponse } from 'data_sources/constants';
import Modal from 'components/_layouts/Modal';
import errorHelper from 'helpers/errorHelper';
import { GIQChoice, GIQuestion } from 'views/App/Portal/Policy/_types/giq';
import TextEntityPicker from '../TextEntityPicker';
import AnswersInput from '../AnswersInput';
import Loading from 'views/App/Portal/Loading';
import { Delete } from '@mui/icons-material';

export type EditQuestionProps = {
  deletable: boolean;
  questionId: string;
  usedEntities: string[]; // List of used text entity IDs
  setLastQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
  setEditQuestionId: React.Dispatch<React.SetStateAction<string | null>>;
};

const EditQuestion: React.FC<EditQuestionProps> = ({
  deletable,
  questionId,
  usedEntities,
  setLastQuestionId,
  setEditQuestionId,
}: EditQuestionProps) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { manual } = useContext(DraftManualGIQContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);

  const giqQuery = useGIQ(manual.id, questionId);
  const giqQuestionSchema = Yup.object().shape({
    questionType: Yup.mixed<'CUSTOM_INPUT' | 'SINGLE_SELECTION'>()
      .oneOf(['CUSTOM_INPUT', 'SINGLE_SELECTION'])
      .required(`${t('draftManuals.giq.properties.question')} is required`),
    choices: Yup.array().when('questionType', {
      is: (values: any[]) => values.includes('SINGLE_SELECTION'),
      then: () =>
        Yup.array()
          .transform((value, originalValue) => {
            return originalValue.filter((x: GIQChoice) => x.value.length > 0);
          })
          .min(2, 'At least two choices are required for single selection questions'),
      otherwise: () => Yup.array().of(Yup.string()).min(0, 'zero'),
    }),
    question: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('draftManuals.giq.properties.question')} is required`),
    hint: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      }),
    textEntity: Yup.object().required('Text entity is required'),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      questionType: 'CUSTOM_INPUT',
      choices: [],
      question: '',
      hint: '',
    },
    resolver: yupResolver(giqQuestionSchema),
  });

  const watchQuestionType = watch('questionType');
  const watchChoices = watch('choices');

  const handleSave: SubmitHandler<GIQuestion> = (values: GIQuestion) => {
    setIsSaving(true);
    updateQuestion(
      manual.id,
      questionId,
      values,
      async (data) => {
        reset();
        queryClient.invalidateQueries(['draft_manuals_giq']);
        await giqQuery.refetch();
        if (data.id) setLastQuestionId(data.id);
        setEditQuestionId(null);

        setIsSaving(false);
      },
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  const handleDelete = () => {
    setIsSaving(true);
    confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('common.delete'),
      description: t('draftManuals.giq.delete.body'),
    })
      .then(() => {
        deleteQuestion(
          manual.id,
          questionId,
          () => {
            reset();
            queryClient.invalidateQueries(['draft_manuals_giq']);
            setLastQuestionId(null);
            setEditQuestionId(null);
            enqueueSnackbar(parse(t(`draftManuals.giq.delete.success`)), {
              variant: 'success',
              autoHideDuration: 6000,
            });

            setIsSaving(false);
          },
          (errors: ServerErrorResponse) => {
            setIsSaving(false);
            setServerErrors(errors);
          }
        );
      })
      .catch(() => {
        // don't do it
      });
  };

  useEffect(() => {
    if (giqQuery.data?.question) {
      reset(giqQuery.data);
    }
  }, [giqQuery.data, questionId]);

  useEffect(() => {
    if (watchQuestionType !== 'SINGLE_SELECTION') {
      setValue('choices', []);
    }
  }, [watchQuestionType]);

  return (
    <>
      <Modal
        title={t('draftManuals.giq.edit.title')}
        onSubmit={handleSubmit(handleSave)}
        size='md'
        actions={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <Box>
              {deletable && (
                <Button
                  className='icon-only'
                  color='error'
                  onClick={() => handleDelete()}
                >
                  <Delete />
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                color='neutral'
                onClick={() => setEditQuestionId(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  trigger();
                }}
                disabled={isSaving}
                component='button'
                type='submit'
                variant='contained'
              >
                {t('common.save')}
              </Button>
            </Box>
          </Box>
        }
        open={!!questionId}
        onClose={() => setEditQuestionId(null)}
      >
        <>
          {giqQuery.isLoading ? (
            <Loading />
          ) : (
            <>
              {errorHelper.renderAllErrors(serverErrors, errors)}
              <Toolbar
                className='phx-toolbar'
                sx={{ justifyContent: 'space-between' }}
              >
                <Stack
                  direction='row'
                  spacing={2}
                >
                  <Typography>{t('draftManuals.giq.properties.questionType')}</Typography>
                </Stack>
                <Stack
                  direction='row'
                  spacing={2}
                >
                  <Controller
                    name='questionType'
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        sx={{
                          justifyContent: 'space-between',
                        }}
                      >
                        <FormControlLabel
                          key='custom_input'
                          value='CUSTOM_INPUT'
                          control={<Radio />}
                          label={t('draftManuals.giq.properties.CUSTOM_INPUT')}
                        />
                        <FormControlLabel
                          key='single_selection'
                          value='SINGLE_SELECTION'
                          control={<Radio />}
                          label={t('draftManuals.giq.properties.SINGLE_SELECTION')}
                        />
                      </RadioGroup>
                    )}
                  />
                </Stack>
              </Toolbar>
              <Controller
                name='textEntity'
                control={control}
                render={({ field }) => (
                  <TextEntityPicker
                    id='textEntity'
                    {...field}
                    label={t('draftManuals.giq.properties.textEntity')}
                    required
                    fullWidth
                    usedEntities={usedEntities.filter((x) => x !== giqQuery.data?.textEntity?.id)}
                    error={!!errors.textEntity}
                    helperText={errors.textEntity ? errors.textEntity.message : undefined}
                  />
                )}
              />
              <TextField
                id='question'
                key='question'
                label={t('draftManuals.giq.properties.question')}
                variant='outlined'
                required
                fullWidth
                {...register('question')}
                error={!!errors.question}
                helperText={errors.question ? errors.question.message : undefined}
              />
              <TextField
                id='hint'
                key='hint'
                label={t('draftManuals.giq.properties.hint')}
                variant='outlined'
                fullWidth
                {...register('hint')}
                error={!!errors.hint}
                helperText={errors.hint ? errors.hint.message : undefined}
              />
              {watchQuestionType === 'SINGLE_SELECTION' && (
                <Stack spacing={0}>
                  <InputLabel>{t('draftManuals.giq.properties.choices')}</InputLabel>
                  <Controller
                    name='choices'
                    control={control}
                    render={({ field }) => (
                      <AnswersInput
                        id='choices'
                        {...field}
                      />
                    )}
                  />
                </Stack>
              )}
            </>
          )}
        </>
      </Modal>
    </>
  );
};

export default EditQuestion;
